<template lang="pug">
  .product-sub-tile(
    v-if="product.selling_plan_groups && product.selling_plan_groups.length > 0"
  )
    a.product-tile__image-wrapper(
      :href="product.url"
    )
      img.product-tile__image(
        v-lazy="image.src",
        :alt="image.alt"
      )
      img.product-tile__image--hover(
        v-lazy="altImage.src",
        :alt="altImage.alt"
      )
      .product-tile__badge.preheader(
        v-if="isBestSeller"
      ) Best Seller
      .product-tile__badge.preheader(
        v-if="isNewProduct"
      ) New Product
      .product-tile__badge.preheader(
        v-if="isNewProductRoast"
      ) New Roast
    .product-sub-tile__content(
      :class="tileClasses"
    )
      a(
        :href="product.url"
      )
        product-bottom-line(
          :product-id="product.id"
          :display-no-reviews="false"
        )
        h3.product-sub-tile__title {{ cleanedTitle }}
        p.product-tile__price
          span.product-tile__price--original {{ originalPrice | money }}
          span.product-tile__price--discounted {{ selectedPrice | money }}
          span.product-tile__price--discount-percent {{ savingsText }}
        product-roast(
          :level="getRoastLevel"
        )
      product-form(
        v-if="displayForm"
        :product="product"
        :default-to-subscription="true"
        @productVariantPrice="setSelectedVariantPrice"
      )
      primary-button.start-subscription(
        v-else
        secondary
        label="Get Started"
        @click="toggleForm"
      )
</template>

<script>
  import { getSizedImageUrl } from 'scripts/helpers/images.js';
  import { productIsBestSeller, productIsNew, productIsNewRoast } from 'scripts/helpers/tags.js';

  import ProductForm from 'scripts/components/product/ProductForm.vue';
  import ProductRoast from 'scripts/components/product/ProductRoast.vue';
  import ProductBottomLine from 'scripts/components/product/ProductBottomLine.vue';
  import PrimaryButton from 'scripts/components/buttons/PrimaryButton.vue';

  export default {
    name: "ProductSubscriptionTile",
    props: {
      product: Object,
    },
    components: {
      ProductForm,
      PrimaryButton,
      ProductRoast,
      ProductBottomLine,
    },
    data() {
      return {
        displayForm: false,
        selectedPrice: 0,
      };
    },
    mounted () {
      // Set initial price based on first variant
      if (this.product.variants && this.product.variants.length > 0) {
        this.selectedPrice = this.product.variants[0].price;
      }
    },
    computed: {
      altImage () {
        if (this.product.images.length < 2) return this.image;

        const image = this.product.images[1];
        const src = getSizedImageUrl(image.src, '300x');
        return { src, alt: image.alt };
      },
      image() {
        const image = this.product.images[0];
        const src = getSizedImageUrl(image.src, '300x');
        return { src, alt: image.alt };
      },
      tileClasses() {
        return this.displayForm ? 'form-visible' : '';
      },
      getRoastLevel() {
        return this.product.tags.reduce((level, tag) => {
          if (tag.includes('roast:')) {
            return tag.split(':')[1];
          }
          return level;
        }, 'medium');
      },
      cleanedTitle() {
        const title = this.product.title;
        if (!title.includes(' - ')) return title;
        return title.split(' - ')[0].trim();
      },
      isBestSeller() {
        return productIsBestSeller(this.product.tags);
      },
      isNewProduct() {
        return productIsNew(this.product.tags);
      },
      isNewProductRoast() {
        return productIsNewRoast(this.product.tags);
      },
      savingsText() {
        // Get discount from selling plans if available
        if (this.product.selling_plan_groups && this.product.selling_plan_groups.length > 0) {
          const firstPlan = this.product.selling_plan_groups[0].selling_plans[0];
          if (firstPlan && firstPlan.price_adjustments && firstPlan.price_adjustments.length > 0) {
            const adjustment = firstPlan.price_adjustments[0];
            if (adjustment.value_type === 'percentage') {
              return `${adjustment.value}% Savings`;
            }
          }
        }
        return '';
      },
      originalPrice() {
        // Calculate original price based on selling plan discount
        if (this.product.selling_plan_groups && this.product.selling_plan_groups.length > 0) {
          const firstPlan = this.product.selling_plan_groups[0].selling_plans[0];
          if (firstPlan && firstPlan.price_adjustments && firstPlan.price_adjustments.length > 0) {
            const adjustment = firstPlan.price_adjustments[0];
            if (adjustment.value_type === 'percentage') {
              const discountAt = 1 - adjustment.value / 100;
              return this.selectedPrice / discountAt;
            }
          }
        }
        return this.selectedPrice;
      }
    },
    methods: {
      toggleForm() {
        this.displayForm = true;
      },
      setSelectedVariantPrice(event) {
        if (event.productId === this.product.id) {
          this.selectedPrice = event.price;
        }
      },
    },
  };
</script>

<style lang="scss">
  .product-sub-tile {
    margin: 0 auto 65px;
    padding: 0 10px;
    width: 100%;
    max-width: 600px;
    display: flex;
    flex-direction: column;
    align-items: center;

    @include tablet-up {
      flex-direction: row;
      align-items: flex-start;
    }

    @include hd-up() {
      padding: 0 30px;
    }

    .product-sub-tile__title {
      margin: 0;
    }

    .product-form {
      margin-top: 0;
    }

    .product-tile__price {
      text-align: left;
      margin: 5px 0;
    }

    .product-tile__price--original {
      color: $color-grey;
      text-decoration: line-through;
    }

    .product-tile__price--original,
    .product-tile__price--discount-percent,
    .product-tile__price--discounted {
      font-size: rem(14);
    }

    .product-tile__price--discount-percent,
    .product-tile__price--discounted {
      margin-left: 10px;
      color: $color-red;
    }

    .product-form__tile-options {
      display: none;
    }

    .product-sub-tile__content {
      margin-top: 15px;
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
      flex-grow: 1;

      @include tablet-up {
        margin-top: 0;
        margin-left: $grid-gutter;
        width: auto;
      }

      &.form-visible {
        justify-content: space-between;
      }
    }

    .product-tile__image--hover,
    .product-tile__image {
      display: block;
      max-width: 258px;
    }

    .product-tile__image-wrapper {
      position: relative;

      &:hover .product-tile__image--hover {
        opacity: 1;
      }
    }

    .product-tile__image--hover {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      opacity: 0;
      object-fit: cover;
      background-color: $color-white;
      transition: opacity 0.3s;
    }

    .button {
      padding-left: 0;
      padding-right: 0;
      min-width: 0;
    }
  }

  .start-subscription {
    margin-top: 2 * $grid-gutter;
  }

  .product-tile__badge {
    position: absolute;
    left: 0px;
    top: 30px;
    border-radius: 100%;
    border: 1px solid $color-indigo;
    padding: 0 10px;
    width: 54px;
    height: 54px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    font-size: rem(13);
    line-height: 13px;
    font-family: $font-acumin-pro;
    font-weight: 500;
    text-transform: uppercase;
    color: $color-indigo;
    background-color: $color-white;

    @include tablet-up {
      font-size: rem(16);
      line-height: 16px;
      left: 30px;
      width: 75px;
      height: 75px;
    }
  }
</style>
